import Rollbar from 'rollbar';

const environment = import.meta.env.VITE_ROLLBAR_ENVIRONMENT ?? 'local';
const codeVersion = import.meta.env.VITE_CODE_VERSION ?? '';

interface RollbarPayload extends Rollbar.Dictionary {
  message?: string;
  body?: { telemetry?: { body?: { message?: string } }[] };
}

/**
 * Firebase error messages meaning "the request never got a response" rather
 * than "the app is broken". With uncaughtErrorLevel: 'critical', an unhandled
 * rejection carrying one of these pages on an ordinary dropped connection.
 */
const FIREBASE_TRANSPORT_ERRORS = new Set([
  'deadline-exceeded',
  'unavailable',
  'cancelled',
  'aborted',
]);

const transmit =
  !(window as Window & { isPlaywrightTest?: boolean }).isPlaywrightTest &&
  !window.location.origin.includes('localhost');

export const rollbarConfig: Rollbar.Configuration = {
  accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN_CLIENT,
  captureUncaught: true,
  captureUnhandledRejections: true,
  uncaughtErrorLevel: 'critical',
  environment,
  verbose: true,
  scrubTelemetryInputs: true,
  captureIp: false,
  captureEmail: false,
  transmit,
  scrubFields: [
    'key',
    'email',
    'search',
    'address',
    'dob',
    'ssn',
    'phone',
    'firstName',
    'lastName',
    'engagement',
    'profile',
  ],
  payload: {
    client: {
      javascript: {
        code_version: codeVersion,
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
  checkIgnore: (_isUncaught, _args, payload: RollbarPayload) => {
    /* filters out "[unhandledrejection] error getting `reason` from event" caused by an empty promise from reCAPTCHA */
    return (
      (!payload.message &&
        payload.body?.telemetry?.some((item) =>
          item.body?.message?.includes('appCheck/recaptcha-error')
        )) ||
      false
    );
  },
  transform: (payload: Rollbar.Payload): void => {
    const trace = payload.body?.trace;
    if (trace?.frames) {
      trace.frames.forEach((frame: { filename?: string }) => {
        if (frame.filename) {
          frame.filename = frame.filename.replace(
            /https?:\/\/[a-zA-Z0-9.-]+(:\d+)?/,
            `https://ec-${environment}-rollbar-alias/dist`
          );
        }
      });
    }

    /* Downgrade recaptcha noise we log ourselves. This only matches message
       items (rollbar.warning/info); thrown Errors arrive as trace items and
       still report at uncaughtErrorLevel ('critical'). */
    if (payload.body?.message?.body?.toLowerCase().includes('recaptcha')) {
      payload.level = 'warning';
    }

    /* The check above only inspects message items, so exception items arrive at
       uncaughtErrorLevel ('critical'). Downgrade transport-level Firebase
       failures: they mean the client lost its connection mid-request, not that
       the app is broken. */
    const exception = trace?.exception as
      | { class?: string; message?: string }
      | undefined;
    if (
      exception?.class === 'FirebaseError' &&
      FIREBASE_TRANSPORT_ERRORS.has(exception.message?.toLowerCase() ?? '')
    ) {
      payload.level = 'warning';
    }
  },
  autoInstrument: {
    contentSecurityPolicy: true,
    errorOnContentSecurityPolicy: true,
  },
};

export const rollbar = new Rollbar(rollbarConfig);

export const addToRollbarContext = (payload: { [key: string]: unknown }) => {
  rollbar.configure({ payload });
};
